// iter-30 Phase 1 — Creator-level LLM Routing overrides.
// Shown on the Creator Dashboard → Operations tab. Lets a creator override
// any of the 7 surfaces above the platform default. Per-silo overrides still
// win on top of these.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Compass, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function CreatorLLMRoutingCard() {
  const [data, setData] = useState(null);    // {surfaces, overrides, resolved}
  const [registry, setRegistry] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [r, reg] = await Promise.all([
        api.get("/me/llm-routing"),
        api.get("/models/registry"),
      ]);
      setData(r.data);
      setRegistry(reg.data);
    } catch (e) {
      // Stable toast ID prevents double-firing on remount / StrictMode.
      toast.error(e.response?.data?.detail || "Couldn't load LLM routing", {
        id: "creator-llm-routing-load-failed",
      });
    }
  };
  useEffect(() => { reload(); }, []);

  const allChoices = useMemo(() => {
    if (!registry?.providers) return [];
    const list = [{ value: "", label: "Inherit from platform default" }];
    for (const p of registry.providers) {
      for (const m of (p.models || [])) {
        list.push({ value: `${p.provider}|${m}`, label: `${p.label} · ${m}`, provider: p.provider, model: m });
      }
    }
    return list;
  }, [registry]);

  const onPick = async (sid, value) => {
    setBusy(true);
    try {
      let body = { model: null, provider: null };
      if (value) {
        const [provider, model] = value.split("|");
        body = { model, provider };
      }
      await api.patch(`/me/llm-routing/${sid}`, body);
      await reload();
      toast.success(value ? `Override saved: ${sid}` : `Cleared override for ${sid}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!data?.overrides || Object.keys(data.overrides).length === 0) {
      toast.info("No overrides to clear.");
      return;
    }
    if (!window.confirm("Clear ALL of your creator-level LLM overrides? Surfaces will inherit from the platform.")) return;
    setBusy(true);
    try {
      for (const sid of Object.keys(data.overrides)) {
        await api.patch(`/me/llm-routing/${sid}`, { model: null, provider: null });
      }
      await reload();
      toast.success("All overrides cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  if (!data || !registry) {
    return (
      <div className="bg-white border hairline rounded-xl p-5 shadow-sm" data-testid="creator-llm-routing-loading">
        <div className="text-sm text-[#6B6A68]">Loading LLM routing…</div>
      </div>
    );
  }

  const overrideCount = Object.keys(data.overrides || {}).length;

  return (
    <div className="bg-white border hairline rounded-xl p-5 shadow-sm" data-testid="creator-llm-routing-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center">
            <Compass size={18} weight="duotone" />
          </div>
          <div>
            <h3 className="font-serif-display text-xl leading-none">LLM Routing</h3>
            <p className="text-xs text-[#6B6A68] mt-1">
              Override the platform model on any of the 7 surfaces. Per-silo
              settings still win on top of these.
            </p>
          </div>
        </div>
        {overrideCount > 0 && (
          <button onClick={clearAll} disabled={busy} data-testid="creator-llm-routing-clear-all"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#C16E5A] flex items-center gap-1 text-[#6B6A68] hover:text-[#C16E5A]">
            <ArrowsCounterClockwise size={12} /> Clear all overrides
          </button>
        )}
      </div>

      <div className="divide-y hairline" data-testid="creator-llm-routing-list">
        {data.surfaces.map((s) => {
          const resolved = data.resolved?.[s.id] || {};
          const isOverridden = resolved.source === "creator";
          const value = isOverridden ? `${resolved.provider}|${resolved.model}` : "";
          return (
            <div key={s.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`creator-llm-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-[#1C1B1A] font-medium">{s.label}</div>
                  {isOverridden && (
                    <span className="text-[9px] uppercase tracking-[0.2em] bg-[#FFF7F1] text-[#C16E5A] px-1.5 py-0.5 rounded">
                      override
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#6B6A68] mt-0.5">
                  Currently · <span className="font-mono text-[#1C1B1A]">{resolved.model || "—"}</span>
                  {!isOverridden && <span className="ml-1.5">(from platform default)</span>}
                </div>
              </div>
              <select
                value={value}
                onChange={(e) => onPick(s.id, e.target.value)}
                disabled={busy}
                data-testid={`creator-llm-select-${s.id}`}
                className="text-xs px-2 py-2 border hairline rounded-md bg-white min-w-[220px]"
              >
                {allChoices.map((c) => (
                  <option key={c.value || "inherit"} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
